<template>
   <div :class="col">
      <div class="mb-0 position-relative sa-field">
         <!-- Label above the box (main.js formStyle 'icons' and 'plain') -->
         <label v-if="label && !floating" :for="inputId" class="form-label">{{ label }}</label>

         <!-- The box + the currency at its right end (the currency sits outside the input-group, so the corners stay round) -->
         <div class="position-relative">
            <div :class="wrapClass" @focusin="onFocus" @focusout="onBlur">
               <!-- Icon in front (formStyle 'icons' only) -->
               <span v-if="showIcon" class="input-group-text"><i :class="icon"></i></span>

               <input
                  v-bind="$attrs"
                  :id="inputId"
                  type="text"
                  inputmode="decimal"
                  autocomplete="off"
                  data-type="money"
                  :data-decimals="decimals"
                  :data-allow-negative="allowNegative ? '' : null"
                  :value="text"
                  :placeholder="floating ? ' ' : placeholder"
                  :style="{ paddingRight: currencySpace + ' !important' }"
                  @input="onInput"
                  class="form-control custom-tooltip-input text-end"
               />

               <!-- Floating label (formStyle 'floating') -->
               <label v-if="floating" :for="inputId" class="sa-float-label">{{ label }}</label>
            </div>

            <!-- The currency, e.g. 1,250.00 KES (the input keeps room for it, so no digit hides behind it) -->
            <span class="sa-money-currency" :style="currencyStyle">{{ currencyCode }}</span>
         </div>

         <small v-if="hint" class="text-muted d-block sa-core-color">{{ hint }}</small>

         <!-- Anything else under the box, e.g. a "Plan price: … use" link -->
         <slot />

         <!-- Above the box: on focus, and in red while the field is invalid -->
         <div v-if="tooltip" class="custom-tooltip">{{ tooltip }}<span class="tooltip-arrow"></span></div>
      </div>
   </div>
</template>

<script>
// An amount of money.
//   <MoneyField v-model="formInputs.amount" label="Amount" icon="fa fa-money" min="1"
//               tooltip="Enter the amount, e.g. 1,250.00" required col="col-md-6" />
// v-model = a number (1250.5), or null while empty / not a valid amount. Send it as it is.
// While typing: commas added at once (1,250.5). When you leave the box: 1,250.50.
// currency: the company's (KES for Kenyan companies, USD for others: $companyCurrency), or set your own.
// decimals: digits after the dot (default 2; 0 for whole amounts). allow-negative: refunds, adjustments.
// min, max, required ... go to the <input> and are checked by $validateField.
let nextId = 0;

export default {
   name: "MoneyField",
   inheritAttrs: false,
   props: {
      modelValue: { default: null },
      label: { type: String, default: "" },
      icon: { type: String, default: "" },
      placeholder: { type: String, default: "" },
      tooltip: { type: String, default: "" },
      hint: { type: String, default: "" },
      col: { type: String, default: "col-md-6" },
      currency: { type: String, default: "" },
      decimals: { type: [String, Number], default: 2 },
      allowNegative: { type: Boolean, default: false },
      id: { type: String, default: "" },
   },
   emits: ["update:modelValue"],
   data() {
      return { autoId: "money-field-" + ++nextId, focused: false, text: "" };
   },
   computed: {
      inputId() {
         return this.id || this.autoId;
      },
      formStyle() {
         return this.$formStyle || "icons";
      },
      floating() {
         return this.formStyle === "floating" && !!this.label;
      },
      showIcon() {
         return this.formStyle === "icons" && !!this.icon;
      },
      currencyCode() {
         if (this.currency) return this.currency.toUpperCase();
         return typeof this.$companyCurrency === "function" ? this.$companyCurrency() : "KES";
      },
      // Room on the right of the input for the currency: its letters + a gap
      currencySpace() {
         return `calc(${this.currencyCode.length}ch + 1.5rem)`;
      },
      currencyStyle() {
         return {
            position: "absolute",
            right: "0.75rem",
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: "0.85em",
            opacity: 0.7,
            pointerEvents: "none",
            zIndex: 6,
         };
      },
      wrapClass() {
         const base = { "sa-money": true, "position-relative": true };
         if (this.showIcon) return { ...base, "input-group": true };
         if (this.floating) {
            return { ...base, "sa-float": true, "is-up": this.focused || this.text !== "", "is-focused": this.focused };
         }
         return base;
      },
   },
   watch: {
      // Set from outside (Edit, Sample, Reset): show it formatted
      modelValue: {
         immediate: true,
         handler(value) {
            if (this.focused) return;
            this.text = this.format(value);
         },
      },
   },
   methods: {
      // 1250.5 -> "1,250.50"
      format(value) {
         if (value === null || value === undefined || value === "") return "";
         const number = Number(value);
         if (!Number.isFinite(number)) return String(value);
         const places = Number(this.decimals) || 0;
         return number.toLocaleString("en-US", { minimumFractionDigits: places, maximumFractionDigits: places });
      },
      // "1,250.50" -> 1250.5 (null if it isn't an amount)
      parse(text) {
         const cleaned = String(text ?? "").replace(/,/g, "").replace(/\s/g, "");
         return /^-?\d+(\.\d+)?$/.test(cleaned) ? Number(cleaned) : null;
      },
      // While typing: commas are added at once (1234567 -> 1,234,567), the cursor stays where it was
      onInput(event) {
         const input = event.target;
         const raw = input.value;
         const caret = input.selectionStart ?? raw.length;
         // How many digits / dots / minus were before the cursor (commas don't count)
         const before = raw.slice(0, caret).replace(/[^\d.-]/g, "").length;

         this.text = this.live(raw);
         input.value = this.text;

         // Put the cursor back after the same number of digits
         let seen = 0;
         let position = 0;
         while (position < this.text.length && seen < before) {
            if (/[\d.-]/.test(this.text[position])) seen++;
            position++;
         }
         try {
            input.setSelectionRange(position, position);
         } catch (e) {
            // some browsers refuse on inputmode boxes: the cursor just goes to the end
         }

         this.$emit("update:modelValue", this.parse(this.text));
      },
      // "1234567.5" -> "1,234,567.5" (keeps a dot just typed, at most `decimals` digits after it)
      live(raw) {
         let value = String(raw ?? "").replace(/[^\d.-]/g, "");
         const negative = this.allowNegative && value.startsWith("-");
         value = value.replace(/-/g, "");

         const places = Number(this.decimals) || 0;
         const dot = value.indexOf(".");
         let whole = dot === -1 ? value : value.slice(0, dot);
         let fraction = dot === -1 ? null : value.slice(dot + 1).replace(/\./g, "").slice(0, places);
         if (places === 0) fraction = null;

         whole = whole.replace(/^0+(?=\d)/, ""); // "007" -> "7"
         whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

         return (negative ? "-" : "") + whole + (fraction !== null ? "." + fraction : "");
      },
      onFocus() {
         this.focused = true;
      },
      // Leaving the box: the full format, e.g. "1,250.5" -> "1,250.50"
      onBlur() {
         this.focused = false;
         const amount = this.parse(this.text);
         if (amount !== null) this.text = this.format(amount);
      },
   },
};
</script>